import {ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit} from '@angular/core';
import {Router} from '@angular/router';
import {DocumentFragment} from 'app/fragment/types';
import {PermissionsService} from 'app/permissions/permissions.service';
import {CarsAction} from 'app/permissions/types/permissions';
import {PrintService} from 'app/print/print.service';
import {DocumentService} from 'app/services/document.service';
import {InternalReferenceService} from 'app/services/internal-reference.service';
import {UUID} from 'app/utils/uuid';
import {take} from 'rxjs';
import {ReferenceSummaryService} from './reference-summary.service';
import {ReferenceSummaryRow} from './types/reference-summary-row';
import {ReferenceSummaryType} from './types/reference-summary-type';

@Component({
  selector: 'cars-reference-summary',
  templateUrl: './reference-summary.component.html',
  styleUrls: ['./reference-summary.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReferenceSummaryComponent implements OnInit {
  public incomingReferences: ReferenceSummaryRow[] = [];
  public outgoingReferences: ReferenceSummaryRow[] = [];
  public sameDocumentReferences: ReferenceSummaryRow[] = [];

  public loading: boolean;
  public documentFragment: DocumentFragment;

  public printDisabled: boolean = false;
  public ReferenceSummaryType: typeof ReferenceSummaryType = ReferenceSummaryType;

  private _documentId: UUID;

  constructor(
    private _router: Router,
    private _printService: PrintService,
    private _referenceSummaryService: ReferenceSummaryService,
    private _internalReferenceService: InternalReferenceService,
    private _documentService: DocumentService,
    private _permissionsService: PermissionsService,
    private _cdr: ChangeDetectorRef
  ) {}

  public ngOnInit(): void {
    this._documentId = this._documentService.getSelected().id;

    const shouldTriggerPrintEvent: boolean = this._router.url.includes('print');

    this.tryRefreshReferencesAndGenerateSummary(shouldTriggerPrintEvent);

    this._documentService.load(this._documentId, {projection: 'FULL_TREE'}, true).then((doc) => {
      this.documentFragment = doc;

      // Set the selected document to trigger all fragments to be indexed
      this._documentService.setSelected(doc);
      this._cdr.markForCheck();
    });
  }

  public setPrintDisabled(): void {
    this.printDisabled = true;
  }

  /**
   * Routes to the correct url on printing
   */
  public printRoute(): void {
    this._router.navigateByUrl(this._router.url.split('/').slice(0, -1).join('/') + '/print' + '/reference-summary');
  }

  /**
   * Routes to the correct url on closing
   */
  public closeRoute(): void {
    this._router.navigateByUrl(this._router.url.split('/').slice(0, -1).join('/'));
  }

  public tryRefreshReferencesAndGenerateSummary(shouldTriggerPrintEvent: boolean = false) {
    this.loading = true;
    this._permissionsService
      .can(CarsAction.AUTHOR_DOCUMENT, this._documentId)
      .pipe(take(1))
      .subscribe((canAuthor) => {
        if (!!canAuthor && !shouldTriggerPrintEvent) {
          this._internalReferenceService.refreshDocumentReferences(this._documentId).then(() => {
            this._initialiseReferences(false);
          });
        } else {
          this._initialiseReferences(shouldTriggerPrintEvent);
        }
      });
  }

  private _initialiseReferences(shouldTriggerPrintEvent: boolean): void {
    this.incomingReferences = [];
    this.outgoingReferences = [];
    this.sameDocumentReferences = [];

    this._referenceSummaryService.fetchReferenceSummaryForDocument(this._documentId).subscribe((refSummaries) => {
      refSummaries.forEach((refSum) => {
        switch (refSum.referenceSummaryType) {
          case ReferenceSummaryType.INCOMING:
            this.incomingReferences.push(refSum);
            break;
          case ReferenceSummaryType.OUTGOING:
            this.outgoingReferences.push(refSum);
            break;
          case ReferenceSummaryType.SAME_DOCUMENT:
            this.sameDocumentReferences.push(refSum);
            break;
        }
      });
      this._cdr.markForCheck();
      this.loading = false;
      if (shouldTriggerPrintEvent) {
        this._printService.triggerPrintEvent();
      }
    });
  }

  /**
   * Formats the header for the reference summary when printing
   */
  public getReferenceSummaryPrintHeader(): string {
    return `CARS Reference Summary`;
  }
}
